import type { ReactNode } from 'react';

/**
 * The onboarding grammar from the flow doc's "Shared visual grammar" table:
 * #F1F2F6 ground, a back chevron + white progress track (+ optional ✕) top bar,
 * Inter-ExtraBold titles, white radius-16 cards, and a full-width 56px purple
 * pill button. Every wizard screen is built from these and nothing else.
 */

function Chevron() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 5l-7 7 7 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Cross() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function FlowScreen({
  progress,
  onBack,
  onClose,
  children,
  footer,
}: {
  /** 0–100 fills the track; undefined hides the bar (Calculating has none). */
  progress?: number;
  onBack?: () => void;
  onClose?: () => void;
  children: ReactNode;
  /** Pinned under the content: the primary/secondary buttons. */
  footer?: ReactNode;
}) {
  return (
    <div className="flow">
      <div className="flow-top">
        {onBack !== undefined ? (
          <button type="button" className="flow-icon" aria-label="Back" onClick={onBack}>
            <Chevron />
          </button>
        ) : (
          <span className="flow-icon-spacer" />
        )}
        {progress !== undefined ? (
          <div className="flow-track" role="progressbar" aria-valuenow={Math.round(progress)}>
            <div className="flow-fill" style={{ width: `${String(progress)}%` }} />
          </div>
        ) : (
          <div className="flow-track-spacer" />
        )}
        {onClose !== undefined ? (
          <button type="button" className="flow-icon" aria-label="Close" onClick={onClose}>
            <Cross />
          </button>
        ) : (
          <span className="flow-icon-spacer" />
        )}
      </div>
      <div className="flow-body">{children}</div>
      {footer !== undefined && <div className="flow-footer">{footer}</div>}
    </div>
  );
}

export function FlowTitle({ children }: { children: ReactNode }) {
  return <h1 className="flow-title">{children}</h1>;
}

export function FlowSub({ children }: { children: ReactNode }) {
  return <p className="flow-sub">{children}</p>;
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="btn-primary" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="btn-secondary" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/** The tappable selection card — purpose options, quiz answers, plan rows. */
export function OptionCard({
  children,
  selected,
  onClick,
  checkbox,
  disabled,
}: {
  children: ReactNode;
  selected: boolean;
  onClick: () => void;
  /** Multi-select rows render the 22px square check, per the quiz spec. */
  checkbox?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={selected ? 'option-card selected' : 'option-card'}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
    >
      <span className="option-label">{children}</span>
      {checkbox === true && (
        <span className={selected ? 'option-check selected' : 'option-check'} aria-hidden="true">
          {selected && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M4.5 12.5l5 5 10-11"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      )}
    </button>
  );
}

/** The purple-tinted pill — "Estimated time to complete: 1 minute". */
export function Pill({ children }: { children: ReactNode }) {
  return <span className="flow-pill">{children}</span>;
}

/** Icon + heading + body, white card — the facial-signature intro rows. */
export function IconCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="icon-card">
      <span className="icon-card-glyph" aria-hidden="true">
        {icon}
      </span>
      <span className="icon-card-text">
        {title !== undefined && <strong>{title}</strong>}
        <span>{children}</span>
      </span>
    </div>
  );
}

/** The flow's input: white card, radius 16, 56px, label above. */
export function FlowField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled,
  autoComplete,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  autoComplete?: string;
}) {
  return (
    <label className="flow-field">
      <span className="flow-field-label">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/** The pulsing brand mark — Splash and the two "hang tight" bridges. */
export function BrandPulse({ label }: { label?: string }) {
  return (
    <div className="brand-pulse">
      <span className="brand-pulse-ring" aria-hidden="true" />
      <span className="brand-pulse-mark" aria-hidden="true">
        <svg width="44" height="44" viewBox="0 0 16 16" fill="none">
          <path d="M8 1.8 13 4v4.5c0 2.9-2 4.5-5 5.5-3-1-5-2.6-5-5.5V4z" fill="#380E99" />
        </svg>
      </span>
      {label !== undefined && <p className="flow-sub" style={{ textAlign: 'center' }}>{label}</p>}
    </div>
  );
}
